import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AdminService } from '../admin.service';
import { ScaleOfFinanceService } from './scale-of-finance.service';
import { DialogData, OpenAdminDialogYesOrNo } from '../admin-yes-or-no/admin-yes-or-no.component';

function integerPattern(): ValidatorFn {
  return (control: AbstractControl) => {
    if (control.value === null || control.value === undefined || control.value === '') return null;
    const ok = /^[0-9]+$/.test(String(control.value));
    return ok ? null : { integer: true };
  };
}

function maxAllowedValidator(maxValueProvider: () => number): ValidatorFn {
  return (control: AbstractControl) => {
    if (control.value === null || control.value === undefined || control.value === '') return null;
    const max = maxValueProvider();
    if (typeof max !== 'number' || Number.isNaN(max)) return null;
    const value = Number(control.value);
    if (value > max) return { maxAllowed: { max } };
    return null;
  };
}

@Component({
  selector: 'scale-of-finance',
  templateUrl: './scale-of-finance.component.html',
  styleUrls: ['./scale-of-finance.component.scss'],
  providers: [ScaleOfFinanceService, AdminService],
})
export class ScaleOfFinanceComponent implements OnInit {
  sofForm: UntypedFormGroup;

  heading: string;
  subHeadingOne: string;
  subHeadingTwo: string;

  crops: any[] = [];
  districts: any[] = [];
  kharifCrops: any[] = [];
  rabiCrops: any[] = [];
  summerCrops: any[] = [];

  loading = false;
  maxAllowedAdditionalSof: number = 100;

  // keep these derived values similar to original imperative code
  selectedDistrict: string | null = null;
  sofAmount: number | null = null; // corresponds to selectedCrop.sofAmount

  constructor(
    private fb: UntypedFormBuilder,
    private adminservice: AdminService,
    private snackbar: MatSnackBar,
    private dialog: MatDialog,
    private scaleOfFinanceService: ScaleOfFinanceService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.initForm();

    this.heading = 'SOF';
    this.subHeadingOne = this.adminservice.getBranchName();
    this.subHeadingTwo = this.adminservice.getSolID();

    this.loadDistricts();
  }

  private initForm() {
    this.sofForm = this.fb.group({
      seasonGroup: this.fb.group({
        cropType: [null, Validators.required],
      }),
      selectedCrop: [null, Validators.required],
      originalSofAmount: [{ value: null, disabled: true }],
      additionalSof: [
        null,
        [
          // dynamic validators applied/updated later when crop is selected
          Validators.min(1),
          integerPattern(),
          // maxAllowedValidator will be added dynamically since it depends on maxAllowedAdditionalSof
        ],
      ],
      revisedSof: [{ value: null, disabled: true }],
    });

    // ensure when selectedCrop is cleared we reset dependent controls
    this.sofForm.get('selectedCrop').valueChanges.subscribe((val) => {
      if (!val) {
        this.sofAmount = null;
        this.maxAllowedAdditionalSof = 100;
        this.sofForm.patchValue({ originalSofAmount: null, additionalSof: null, revisedSof: null }, { emitEvent: false });
        this.updateAdditionalValidators();
      }
    });
  }

  // helper to attach/update the dynamic max validator
  private updateAdditionalValidators() {
    const ctrl = this.sofForm.get('additionalSof');
    const validators = [Validators.min(1), integerPattern(), maxAllowedValidator(() => this.maxAllowedAdditionalSof)];
    // If original is zero we will disable the control instead of applying validators; caller handles that.
    ctrl.setValidators(validators);
    ctrl.updateValueAndValidity();
  }

  loadDistricts() {
    this.loading = true;
    this.adminservice.getCropDistricts(sessionStorage.getItem('wl-admin-id')).subscribe(
      (resp: any) => {
        this.loading = false;
        if (resp.status && resp.message === 'success' && resp.results && resp.results.length > 0) {
          this.districts = resp.results.map((d) => ({ name: d.district_name }));
        } else {
          this.snackbar.open(`Error fetching crop districts: ${resp.message}`, 'OK', { duration: 4000 });
        }
      },
      () => {
        this.loading = false;
        this.snackbar.open(`Error fetching crop districts`, 'OK', { duration: 4000 });
      }
    );
  }

  // called by <wl-autocomplete> (same as original)
  selectDistrict(district: any) {
    this.selectedDistrict = district.name;
    this.loading = true;

    // clear form state but keep selectedDistrict
    this.sofForm.reset();
    // reinitialize cropType control (reset won't recreate nested controls)
    this.sofForm.get('seasonGroup.cropType').reset();

    this.getCropsByDistrict();
  }

  getCropsByDistrict() {
    if (!this.selectedDistrict) {
      this.loading = false;
      return;
    }
    this.scaleOfFinanceService.getCropListByDistrict(sessionStorage.getItem('wl-admin-id'), this.selectedDistrict).subscribe(
      (resp: any) => {
        this.loading = false;
        if (resp.status && resp.message === 'success') {

          this.kharifCrops = (resp.kharif || []).map((d) => ({ ...d, name: d.crop_name }));
          this.rabiCrops = (resp.rabi || []).map((d) => ({ ...d, name: d.crop_name }));
          this.summerCrops = (resp.summer || []).map((d) => ({ ...d, name: d.crop_name }));

        } else {
          this.snackbar.open(`Error fetching crops for district`, 'OK', { duration: 4000 });
        }
      },
      () => {
        this.loading = false;
        this.snackbar.open(`Error fetching crops for district.`, 'OK', { duration: 4000 });
      }
    );
  }

  onCropTypeChange() {
    const type = this.sofForm.get('seasonGroup.cropType').value;
    if (!type) return;
    if (type === 'Kharif') this.crops = [...this.kharifCrops];
    else if (type === 'Rabi') this.crops = [...this.rabiCrops];
    else if (type === 'Summer') this.crops = [...this.summerCrops];
    else this.crops = [];


    // reset crop-dependent fields (like originalSofAmount / additionalSof / revisedSof)
    this.sofForm.patchValue({ selectedCrop: null, originalSofAmount: null, additionalSof: null, revisedSof: null }, { emitEvent: false });
    this.sofAmount = null;
    this.maxAllowedAdditionalSof = 100;
    this.updateAdditionalValidators();
    this.cdr.detectChanges();
  }

  // selectCrop() {
  //   const crop = this.sofForm.get('selectedCrop').value;
  //   if (!crop) return;

  //   // replicate original behavior
  //   this.sofAmount = Number(crop.sofAmount);
  //   const original = Number(crop.original_irrigated);
  //   this.sofForm.patchValue({ originalSofAmount: original }, { emitEvent: false });

  //   if (original === 0) {
  //     // original code set additionalSof = 0 and warned user
  //     this.sofForm.get('additionalSof').setValue(0);
  //     this.sofForm.get('additionalSof').disable({ emitEvent: false });
  //     this.sofForm.patchValue({ revisedSof: null }, { emitEvent: false });
  //     this.maxAllowedAdditionalSof = 100; // irrelevant but keep sane
  //     this.snackbar.open('Original SOF is 0, please check the data', 'OK', { duration: 4000 });
  //   } else {
  //     // compute percentage diff like original
  //     const additional = this.roundToTwo(((this.sofAmount - original) / original) * 100);
  //     this.maxAllowedAdditionalSof = 100 - additional;
  //     this.sofForm.get('additionalSof').enable({ emitEvent: false });
  //     this.sofForm.get('additionalSof').setValue(additional);
  //     this.updateAdditionalValidators();

  //     // compute revised using original formula: revised = sofAmount + (original * additional)/100
  //     const revisedSofAmount = (original * additional) / 100;
  //     const revised = Math.round(this.sofAmount + revisedSofAmount);
  //     this.sofForm.patchValue({ revisedSof: revised }, { emitEvent: false });
  //   }
  // }

  selectCrop() {
  const cropName = this.sofForm.get('selectedCrop').value;
  const crop = this.crops.find(c => c.name === cropName);
  if (!crop) return;

  this.sofAmount = Number(crop.sofAmount);
  const original = Number(crop.original_irrigated);
  this.sofForm.patchValue({ originalSofAmount: original }, { emitEvent: false });

  if (original === 0) {
    // Original SOF is zero → disable additional input, keep revised null
    this.sofForm.get('additionalSof').setValue(0, { emitEvent: false });
    this.sofForm.get('additionalSof').disable({ emitEvent: false });
    this.sofForm.patchValue({ revisedSof: null }, { emitEvent: false });
    this.maxAllowedAdditionalSof = 100;
    this.snackbar.open('Original SOF is 0, please check the data', 'OK', { duration: 4000 });
  } else {
    // Reset additional to 0 always, let user manually change
      const additional = this.roundToTwo(((this.sofAmount - original) / original) * 100);
      this.maxAllowedAdditionalSof = 100 - additional;
      this.sofForm.get('additionalSof').enable({ emitEvent: false });
      this.sofForm.get('additionalSof').setValue(additional);
      this.updateAdditionalValidators();
    this.sofForm.get('additionalSof').enable({ emitEvent: false });
    this.sofForm.get('additionalSof').setValue(0, { emitEvent: false });
    this.updateAdditionalValidators();

    // Revised = original at this point
    this.sofForm.patchValue({ revisedSof: this.sofAmount }, { emitEvent: false });
  }
}


  // onAdditionalSofChange() {
  //   const additionalCtrl = this.sofForm.get('additionalSof');
  //   if (!additionalCtrl || additionalCtrl.disabled) return;
  //   const additional = Number(additionalCtrl.value);
  //   const original = this.sofForm.get('originalSofAmount').value;
  //   if (!additional || !original) {
  //     this.sofForm.patchValue({ revisedSof: null }, { emitEvent: false });
  //     return;
  //   }
  //   // match original: revised = sofAmount + (original * additional)/100
  //   const revised = Math.round(this.sofAmount + (original * additional) / 100);
  //   this.sofForm.patchValue({ revisedSof: revised }, { emitEvent: false });
  //   // update validator state in case additional was changed out of bounds
  //   this.updateAdditionalValidators();
  // }

//   onAdditionalSofChange() {
//   const additionalCtrl = this.sofForm.get('additionalSof');
//   if (!additionalCtrl || additionalCtrl.disabled) return;

//   const additional = Number(additionalCtrl.value);
//   const original = this.sofForm.get('originalSofAmount').value;

//   if (original == null || isNaN(original)) {
//     this.sofForm.patchValue({ revisedSof: null }, { emitEvent: false });
//     return;
//   }

//   // Revised = original + (original * additional%) 
//   const revised = Math.round(original + (original * additional) / 100);
//   this.sofForm.patchValue({ revisedSof: revised }, { emitEvent: false });

//   this.updateAdditionalValidators();
// }

onAdditionalSofChange() {
  const additionalCtrl = this.sofForm.get('additionalSof');
  if (!additionalCtrl || additionalCtrl.disabled) return;

  const additional = Number(additionalCtrl.value);
  const original = Number(this.sofForm.get('originalSofAmount').value);

  if (!additional || !this.sofAmount) {
    this.sofForm.patchValue({ revisedSof: null }, { emitEvent: false });
    return;
  }

  // Same as your template-driven logic
  const revisedSofAmount = (original * additional) / 100;
  const revised = Math.round(this.sofAmount + revisedSofAmount);

  this.sofForm.patchValue({ revisedSof: revised }, { emitEvent: false });

  this.isAdditionalSofValid();
}



  isAdditionalSofValid(): boolean {
    const ctrl = this.sofForm.get('additionalSof');
    if (!ctrl) return false;
    // treat disabled but set to 0 as valid (original did set 0)
    if (ctrl.disabled && Number(ctrl.value) === 0) return true;
    return ctrl.valid;
  }

  get canSubmit(): boolean {
    const cropType = this.sofForm.get('seasonGroup.cropType').value;
    const selectedCrop = this.sofForm.get('selectedCrop').value;
    const original = this.sofForm.get('originalSofAmount').value;
    const additional = this.sofForm.get('additionalSof').value;
    // mirror original button conditions: !cropType || !selectedCrop || !sofAmount || !isAdditionalSofValid() || additional > maxAllowed
    if (!cropType || !selectedCrop || !this.sofAmount) return false;
    if (!this.isAdditionalSofValid()) return false;
    if (Number(additional) > this.maxAllowedAdditionalSof) return false;
    return true;
  }

  submitForm() {
    if (!this.canSubmit) return;

    const cropType = this.sofForm.get('seasonGroup.cropType').value;
    let selectedCrop = this.sofForm.get('selectedCrop').value;
    const crop = this.crops.find(c => c.name === selectedCrop);
    selectedCrop = crop;
    const revisedSof = this.sofForm.getRawValue().revisedSof;
    const additionalSof = this.sofForm.getRawValue().additionalSof;
    const original = this.sofForm.getRawValue().originalSofAmount;

    if (additionalSof > this.maxAllowedAdditionalSof) {
      this.snackbar.open(`Maximum allowed additional SOF is ${this.maxAllowedAdditionalSof}`, 'OK', { duration: 3000 });
      return;
    }

    const updates = {
      prevSof: +original,
      currSof: +revisedSof,
      percentage: +additionalSof,
      season: cropType,
      cropName: selectedCrop.cropNameEnglish,
    };

    this.loading = true;
    this.scaleOfFinanceService.sofUpdate(sessionStorage.getItem('wl-admin-id'), selectedCrop.id, updates)
      .subscribe(
        (response) => {
          this.loading = false;
          this.onUpdate(response.message);
          // reload crops for current selected district (same as original)
          this.getCropsByDistrict();
        },
        (error) => {
          this.loading = false;
          this.onUpdate(`Network error: ${error.message}`);
        }
      );
  }

  private roundToTwo(num) {
    return Math.round(num * 100) / 100;
  }

  private onUpdate(message: string) {
    this.dialog.closeAll();
    const dialogData: DialogData = {
      heading: '',
      content1: message,
      button1Text: 'OK',
      button2Text: '',
      showWaiting: false,
      showBuffering: false,
      content11: '',
      content2: '',
      content3: '',
      content4: '',
      content5: '',
      content6: '',
      content7: '',
      content8: '',
      content9: '',
      content10: ''
    };
    this.dialog.open(OpenAdminDialogYesOrNo, { data: dialogData, width: '400px', disableClose: true })
      .afterClosed().subscribe(() => {
        // reset just like original
        this.sofForm.reset();
        this.sofAmount = null;
        this.maxAllowedAdditionalSof = 100;
      });
  }
}
